// Help / FAQ page strings — namespace `help`
export default {
  en: {
    hero: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to everything from key activation to refunds.',
      searchPlaceholder: "Search for 'How to refund'…",
    },
    // Each entry: key = stable slug (locale-independent), category = topic key
    // from useHelpFaq HELP_TOPICS (orders | payments | refunds | account), q = question,
    // a = answer paragraphs. Used only when the CMS FAQ is empty or unreachable.
    faq: [
      {
        key: 'how-to-activate',
        category: 'orders',
        q: 'How do I activate my game key?',
        a: [
          'To activate a key on Steam, open the Steam client and choose Games → "Activate a Product on Steam…", then paste your key when prompted.',
          'For Epic Games, log into your account via the browser, hover over your profile name, and select "Redeem Code".',
        ],
      },
      {
        key: 'delivery-time',
        category: 'orders',
        q: 'How long does delivery take?',
        a: [
          'Delivery is instant in the vast majority of cases — your key appears on the order page and is emailed to you the moment your payment clears.',
          'Occasionally an order needs supplier confirmation, which can take a few minutes. The order page updates automatically as the status changes.',
        ],
      },
      {
        key: 'region-lock',
        category: 'orders',
        q: 'Are keys regionally locked?',
        a: [
          'Some keys are region-locked and only activate on accounts registered in a specific region; others are region-free (global).',
          'Each product page states its activation region, and the catalog Region filter lets you narrow results once region data is available.',
        ],
      },
      {
        key: 'payment-methods',
        category: 'payments',
        q: 'What payment methods are accepted?',
        a: [
          'EUR card payments (Visa, Mastercard, Maestro) and PayPal, plus your in-app wallet balance.',
          'Card details never touch our servers — payments are processed by Stripe and PayPal.',
        ],
      },
      {
        key: 'vat-included',
        category: 'payments',
        q: 'Is VAT included in prices?',
        a: ['Yes. All listed prices include EU VAT — the price you see is the price you pay.'],
      },
      {
        key: 'refund-policy',
        category: 'refunds',
        q: 'What is your refund policy?',
        a: [
          'Unrevealed keys can be refunded to your wallet within 14 days of purchase.',
          'Once a key is revealed it is considered consumed and is non-refundable — except in the case of a verified supplier defect.',
        ],
      },
      {
        key: 'defective-key',
        category: 'refunds',
        q: 'How do I report a defective key?',
        a: [
          'Open the order page and click "Report issue". Include the activation error message and a screenshot if possible. We escalate to the supplier within one business day.',
        ],
      },
      {
        key: 'secure-account',
        category: 'account',
        q: 'How do I secure my account?',
        a: [
          'Use a unique, strong password and keep your email verified. Change your password from Account → Settings → Password — all other sessions are signed out afterwards.',
        ],
      },
      {
        key: 'kyc',
        category: 'account',
        q: 'What is KYC and when do I need it?',
        a: [
          'KYC is identity verification, required for individual purchases over €50 under EU compliance rules. Submit your details from Account → Settings → Security & KYC.',
        ],
      },
      {
        key: 'bulk-keys',
        category: 'orders',
        q: 'Can I buy keys in bulk?',
        a: [
          'Yes — add multiple units of a product to your cart up to the available stock and per-order limit. For large volume orders, contact support and we will help arrange it.',
        ],
      },
      {
        key: 'payment-failed',
        category: 'payments',
        q: 'Why did my payment fail?',
        a: [
          'The most common reasons are insufficient funds, a declined 3-D Secure challenge, or a country mismatch with your card issuer.',
          'Try a different card or payment method, or contact support with your order ID if it persists.',
        ],
      },
      {
        key: 'key-not-working',
        category: 'orders',
        q: "What if my key doesn't work?",
        a: [
          'Contact support with your order ID. We verify with the supplier and either issue a replacement key or refund your wallet.',
        ],
      },
    ],
    breadcrumb: 'Help',
    resultCount: '{count} answer | {count} answers',
    topics: {
      label: 'Help topics',
      all: 'All topics',
      orders: 'Orders & keys',
      payments: 'Payments & wallet',
      refunds: 'Refunds',
      account: 'Account',
    },
    faqHeading: 'Questions & answers',
    empty: {
      body: 'No answers match your search.',
      clear: 'Clear search',
    },
    contact: {
      heading: 'Still stuck?',
      body: 'Write to us with your order ID and we will sort it out.',
      email: 'Email support',
      orders: 'Go to my orders',
    },
  },
  cs: {
    hero: {
      title: 'Nejčastější dotazy',
      subtitle: 'Najděte odpovědi na vše od aktivace klíče až po vrácení peněz.',
      searchPlaceholder: "Hledat např. 'Jak vrátit peníze'…",
    },
    faq: [
      {
        key: 'how-to-activate',
        category: 'orders',
        q: 'Jak aktivuji herní klíč?',
        a: [
          'Pro aktivaci klíče na Steamu otevřete klienta Steam a zvolte Hry → „Aktivovat produkt na Steamu…", poté vložte klíč, když budete vyzváni.',
          'V případě Epic Games se přihlaste ke svému účtu v prohlížeči, najeďte myší na své jméno a zvolte „Uplatnit kód".',
        ],
      },
      {
        key: 'delivery-time',
        category: 'orders',
        q: 'Jak dlouho trvá doručení?',
        a: [
          'Doručení je ve valné většině případů okamžité — váš klíč se objeví na stránce objednávky a je vám zaslán e-mailem v okamžiku, kdy je platba potvrzena.',
          'Občas objednávka vyžaduje potvrzení od dodavatele, což může trvat několik minut. Stránka objednávky se automaticky aktualizuje podle změny stavu.',
        ],
      },
      {
        key: 'region-lock',
        category: 'orders',
        q: 'Jsou klíče regionálně omezené?',
        a: [
          'Některé klíče jsou regionálně uzamčeny a lze je aktivovat pouze na účtech registrovaných v určitém regionu; jiné jsou globální (bez regionálního omezení).',
          'Každá stránka produktu uvádí region aktivace a filtr Region v katalogu vám umožní výsledky zúžit, jakmile jsou data o regionu k dispozici.',
        ],
      },
      {
        key: 'payment-methods',
        category: 'payments',
        q: 'Jaké platební metody jsou přijímány?',
        a: [
          'Platby kartou v EUR (Visa, Mastercard, Maestro) a PayPal, plus zůstatek ve vaší peněžence v aplikaci.',
          'Údaje o kartě se nikdy nedostanou na naše servery — platby zpracovává Stripe a PayPal.',
        ],
      },
      {
        key: 'vat-included',
        category: 'payments',
        q: 'Je DPH zahrnuta v cenách?',
        a: ['Ano. Všechny zobrazené ceny obsahují DPH pro EU — cena, kterou vidíte, je cena, kterou zaplatíte.'],
      },
      {
        key: 'refund-policy',
        category: 'refunds',
        q: 'Jaká je vaše zásada vrácení peněz?',
        a: [
          'Neodhalené klíče lze vrátit do vaší peněženky do 14 dnů od nákupu.',
          'Jakmile je klíč odhalen, považuje se za spotřebovaný a nelze jej vrátit — s výjimkou ověřené závady ze strany dodavatele.',
        ],
      },
      {
        key: 'defective-key',
        category: 'refunds',
        q: 'Jak nahlásím vadný klíč?',
        a: [
          'Otevřete stránku objednávky a klikněte na „Nahlásit problém". Přiložte chybovou zprávu při aktivaci a pokud možno snímek obrazovky. Případ eskalujeme k dodavateli do jednoho pracovního dne.',
        ],
      },
      {
        key: 'secure-account',
        category: 'account',
        q: 'Jak zabezpečím svůj účet?',
        a: [
          'Používejte jedinečné, silné heslo a udržujte ověřený e-mail. Heslo změníte v části Účet → Nastavení → Heslo — všechny ostatní relace budou poté odhlášeny.',
        ],
      },
      {
        key: 'kyc',
        category: 'account',
        q: 'Co je KYC a kdy ho potřebuji?',
        a: [
          'KYC je ověření totožnosti, které je v souladu s předpisy EU vyžadováno při jednotlivých nákupech nad 50 €. Své údaje odešlete v části Účet → Nastavení → Zabezpečení a KYC.',
        ],
      },
      {
        key: 'bulk-keys',
        category: 'orders',
        q: 'Mohu nakupovat klíče ve větším množství?',
        a: [
          'Ano — přidejte do košíku více kusů produktu až do výše dostupného skladu a limitu na objednávku. Pro velkoobchodní objednávky kontaktujte podporu a my vám pomůžeme vše zařídit.',
        ],
      },
      {
        key: 'payment-failed',
        category: 'payments',
        q: 'Proč moje platba selhala?',
        a: [
          'Nejčastějšími důvody jsou nedostatek prostředků, odmítnutá výzva 3-D Secure nebo neshoda země s vydavatelem karty.',
          'Zkuste jinou kartu nebo způsob platby, případně kontaktujte podporu s ID objednávky, pokud problém přetrvává.',
        ],
      },
      {
        key: 'key-not-working',
        category: 'orders',
        q: 'Co když můj klíč nefunguje?',
        a: [
          'Kontaktujte podporu s ID objednávky. Ověříme situaci u dodavatele a buď vydáme náhradní klíč, nebo vrátíme prostředky do vaší peněženky.',
        ],
      },
    ],
    breadcrumb: 'Nápověda',
    // Czech has three plural forms: 1 / 2–4 / 5+.
    resultCount: '{count} odpověď | {count} odpovědi | {count} odpovědí',
    topics: {
      label: 'Témata nápovědy',
      all: 'Všechna témata',
      orders: 'Objednávky a klíče',
      payments: 'Platby a peněženka',
      refunds: 'Vrácení peněz',
      account: 'Účet',
    },
    faqHeading: 'Otázky a odpovědi',
    empty: {
      body: 'Vašemu hledání neodpovídají žádné odpovědi.',
      clear: 'Vymazat hledání',
    },
    contact: {
      heading: 'Pořád si nevíte rady?',
      body: 'Napište nám s ID objednávky a vyřešíme to.',
      email: 'Napsat podpoře',
      orders: 'Moje objednávky',
    },
  },
};
